import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import InputAmount from '../../../component/input/InputAmount';
import { ReactComponent as AddIcon } from '../../../assets/images/add-circle.svg';
import { useTranslation } from 'react-i18next';
import './AddEditCellSettingsDialog.scss';
import { ProductService } from '../../../service/ProductService';
import useValidator from '../../../utils/validator/useValidator';
import { CellSettingsMode } from '../enums/CellSettingsMode';

const AddEditCellSettingsDialog = ({
    visible,
    onHide,
    onSave,
    cellData,
    mode = CellSettingsMode.ADD,
    onNewProduct,
    reloadKey,
    selectedCells = null,
}) => {
    const { t } = useTranslation();
    const validator = useValidator();
    const [validationAttempted, setValidationAttempted] = useState(false);

    const [formData, setFormData] = useState({
        product: cellData?.cell?.product || null,
        productId: cellData?.cell?.product?.id || null,
        price: cellData?.price || null,
        currency: cellData?.cell?.currency || null,
        effectiveDate: null,
        minQuantity: cellData?.cell?.minQuantity || null,
        maxQuantity: cellData?.cell?.maxQuantity || null,
        mergeCells: cellData?.mergeCells || false,
        categoryColor: cellData?.categoryColor || null,
        categoryName: cellData?.categoryName || null,
        ean: cellData?.ean || null,
        hasPhoto: cellData?.hasPhoto || false,
        mdbId: cellData?.cell?.mdbId || null,
        evaDtsId: cellData?.cell?.evaDtsId || null,
    });

    const [productOptions, setProductOptions] = useState([]);

    useEffect(() => {
        if (!visible) {
            setValidationAttempted(false);
            validator.hideMessages();
            return;
        }

        setValidationAttempted(false);
    }, [visible]);

    useEffect(() => {
        let isMounted = true;

        const fetchProducts = async () => {
            try {
                const data = await ProductService.getPlanogramProductsWithHasPhoto();

                if (!isMounted) {
                    return;
                }

                const options = (Array.isArray(data) ? data : []).map(product => {
                    const id = product.externalId;
                    const name = product.name ?? '-';
                    const weightVolume = product.weightVolume ?? null;
                    const unitOfMeasurement = product.unitOfMeasurement ?? null;

                    const displayName = `${name}${
                        weightVolume
                            ? ` / ${weightVolume}${unitOfMeasurement ? ` ${unitOfMeasurement}` : ''}`
                            : ''
                    }`;

                    return {
                        id,
                        name,
                        weightVolume,
                        unitOfMeasurement,
                        displayName,
                        price: product.price ?? null,
                        currency: product.currency ?? null,
                        minQuantity: product.minQuantity ?? 0,
                        maxQuantity: product.maxQuantity ?? 0,
                        categoryColor: product.categoryColor ?? '#C0C0C0',
                        categoryName: product.category?.name ?? null,
                        ean: product.ean ?? null,
                        hasPhoto: product.hasPhoto ?? false,
                    };
                });

                setProductOptions(options);
            } catch (error) {
                console.error('Failed to fetch planogram products', error);
                setProductOptions([]);
            }
        };

        if (visible) {
            fetchProducts();
        }

        return () => {
            isMounted = false;
        };
    }, [visible, reloadKey]);

    useEffect(() => {
        if (!visible) {
            return;
        }

        const cell = cellData?.cell || null;
        const product = cell?.product || null;

        setFormData(prev => ({
            ...prev,
            product,
            productId: product?.id || null,
            price: cell?.price ?? null,
            currency: cell?.currency ?? null,
            effectiveDate: null,
            minQuantity: cell?.minQuantity ?? null,
            maxQuantity: cell?.maxQuantity ?? null,
            mergeCells: cell?.mergeCells ?? prev.mergeCells,
            categoryColor: cell?.categoryColor ?? prev.categoryColor,
            categoryName: cell?.categoryName ?? prev.categoryName,
            ean: cell?.ean ?? prev.ean,
            hasPhoto: cell?.hasPhoto ?? prev.hasPhoto,
            mdbId: cell?.mdbId ?? prev.mdbId,
            evaDtsId: cell?.evaDtsId ?? prev.evaDtsId,
        }));
    }, [visible, cellData, productOptions]);

    useEffect(() => {
        const isAddMode = mode === CellSettingsMode.ADD;

        if (!visible || !isAddMode) {
            return;
        }

        if (!formData.productId) {
            setFormData(prev => ({
                ...prev,
                price: null,
                currency: null,
                minQuantity: null,
                maxQuantity: null,
                categoryColor: null,
                categoryName: null,
                ean: null,
            }));
        }
    }, [visible, mode, formData.productId]);

    const handleProductChange = event => {
        const selectedOption = productOptions.find(option => option.id === event.value) || null;

        setFormData(prev => ({
            ...prev,
            product: selectedOption,
            productId: selectedOption?.id ?? null,
            price: selectedOption?.price ?? null,
            currency: selectedOption?.currency ?? null,
            minQuantity: selectedOption?.minQuantity ?? null,
            maxQuantity: selectedOption?.maxQuantity ?? null,
            categoryColor: selectedOption?.categoryColor ?? null,
            categoryName: selectedOption?.categoryName ?? null,
            ean: selectedOption?.ean ?? null,
            hasPhoto: selectedOption?.hasPhoto ?? false,
        }));

        validator.showMessage('cell-settings-product');
    };

    const shouldShowValidation = validationAttempted;

    const productErrorEl = shouldShowValidation
        ? validator.message(
            'cell-settings-product',
            t('planogram.cellSettings.product'),
            formData.productId,
            'required',
        )
        : null;

    const minQtyErrorEl = shouldShowValidation
        ? validator.message(
            'cell-settings-min-qty',
            t('planogram.cellSettings.minQuantity'),
            formData.minQuantity,
            'required',
        )
        : null;

    const maxQtyErrorEl = shouldShowValidation
        ? validator.message(
            'cell-settings-max-qty',
            t('planogram.cellSettings.maxQuantity'),
            formData.maxQuantity,
            'required',
        )
        : null;

    const isSaveDisabled =
        !formData.productId ||
        formData.price === null ||
        formData.price === undefined ||
        formData.price === '' ||
        formData.minQuantity === null ||
        formData.minQuantity === undefined ||
        formData.maxQuantity === null ||
        formData.maxQuantity === undefined;

    const handleSave = () => {
        setValidationAttempted(true);

        if (!validator.allValid()) {
            validator.showMessages();
            return;
        }

        const payload = {
            ...formData,
            effectiveDate: null,
        };

        onSave(
            payload,
            mode === CellSettingsMode.BULK_EDIT ? selectedCells : null,
            mode,
        );

        onHide();
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={
                mode === CellSettingsMode.EDIT
                    ? t('planogram.cellSettings.editTitle')
                    : mode === CellSettingsMode.BULK_EDIT
                        ? t('planogram.cellSettings.bulkEditTitle')
                        : t('planogram.cellSettings.newTitle')
            }
            className="cell-settings-dialog"
            modal
            closeOnEscape
            closable
            style={{
                width: 'auto',
            }}
        >
            <div className="flex flex-column gap-4 w-full">
                {mode === CellSettingsMode.BULK_EDIT && (
                    <div className="cells-selected-row flex align-items-center justify-content-between">
                        <label className="font-semibold text-color-secondary text-sm m-0">
                            {t('planogram.cellSettings.cells')}
                        </label>

                        <div className="flex align-items-center gap-2">
                            <span className="cells-selected-label">
                                {t('planogram.cellSettings.selected')}:
                            </span>

                            <span className="cells-selected-count">
                                {selectedCells?.size ?? 0}
                            </span>
                        </div>
                    </div>
                )}

                <div className="flex flex-column gap-2">
                    <label className="font-semibold text-color-secondary text-sm">
                        {t('planogram.cellSettings.product')}
                    </label>

                    <Dropdown
                        id="cell-settings-product"
                        value={formData.productId}
                        options={productOptions.map(option => ({
                            label: option.displayName,
                            value: option.id,
                        }))}
                        onChange={handleProductChange}
                        className={`w-full ${productErrorEl ? 'p-invalid' : ''}`}
                        placeholder={t('planogram.selectProduct')}
                        filter
                    />

                    {productErrorEl}

                    <div className="new-product-link" onClick={() => onNewProduct?.()}>
                        <AddIcon />
                        <span>{t('planogram.cellSettings.newProduct')}</span>
                    </div>
                </div>

                <div className="flex flex-column gap-2">
                    <InputAmount
                        id="cell-settings-price"
                        label={`${t('planogram.cellSettings.price')}${
                            formData.currency ? ` (${formData.currency})` : ''
                        }`}
                        value={formData.price}
                        onChange={event =>
                            setFormData(prev => ({
                                ...prev,
                                price: event.target.value,
                            }))
                        }
                        placeholder="0,00"
                        maxValue={999999}
                        validator={validator}
                        validations="required"
                        showValidation={shouldShowValidation}
                    />
                </div>

                <div className="flex gap-3">
                    <div className="flex flex-column gap-2 flex-1">
                        <label className="font-semibold text-color-secondary text-sm">
                            {t('planogram.cellSettings.minQuantity')}
                        </label>

                        <InputNumber
                            id="cell-settings-min-qty"
                            value={formData.minQuantity}
                            onValueChange={event =>
                                setFormData(prev => ({
                                    ...prev,
                                    minQuantity: event.value,
                                }))
                            }
                            showButtons
                            buttonLayout="horizontal"
                            decrementButtonIcon="pi pi-chevron-down"
                            incrementButtonIcon="pi pi-chevron-up"
                            step={1}
                            min={0}
                            max={9999}
                            useGrouping={false}
                            className={`w-full ${minQtyErrorEl ? 'p-invalid' : ''}`}
                        />

                        {minQtyErrorEl}
                    </div>

                    <div className="flex flex-column gap-2 flex-1">
                        <label className="font-semibold text-color-secondary text-sm">
                            {t('planogram.cellSettings.maxQuantity')}
                        </label>

                        <InputNumber
                            id="cell-settings-max-qty"
                            value={formData.maxQuantity}
                            onValueChange={event =>
                                setFormData(prev => ({
                                    ...prev,
                                    maxQuantity: event.value,
                                }))
                            }
                            showButtons
                            buttonLayout="horizontal"
                            decrementButtonIcon="pi pi-chevron-down"
                            incrementButtonIcon="pi pi-chevron-up"
                            step={1}
                            min={0}
                            max={9999}
                            useGrouping={false}
                            className={`w-full ${maxQtyErrorEl ? 'p-invalid' : ''}`}
                        />

                        {maxQtyErrorEl}
                    </div>
                </div>
            </div>

            <div className="flex w-full mt-5">
                <Button
                    label={t('planogram.changePrice.save')}
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    className="p-button-primary"
                />
            </div>
        </Dialog>
    );
};

export default AddEditCellSettingsDialog;